import { request } from '@core/api/request';
import type {
  TAnnouncementMediaUploadUrlRequest,
  TAnnouncementMediaUploadUrlResponse,
} from '@src/shared/types/announcement';

export const ANNOUNCEMENT_MAX_FILE_SIZE_MB = 50;
export const ANNOUNCEMENT_MAX_FILE_SIZE_BYTES =
  ANNOUNCEMENT_MAX_FILE_SIZE_MB * 1024 * 1024;

export const ANNOUNCEMENT_IMAGE_ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
} as const;

export const ANNOUNCEMENT_VIDEO_ACCEPT = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
} as const;

export const ANNOUNCEMENT_ALLOWED_MIME_TYPES = [
  ...Object.keys(ANNOUNCEMENT_IMAGE_ACCEPT),
  ...Object.keys(ANNOUNCEMENT_VIDEO_ACCEPT),
];

export function isAnnouncementImageMime(mimeType: string) {
  return mimeType.startsWith('image/');
}

export function isAnnouncementVideoMime(mimeType: string) {
  return mimeType.startsWith('video/');
}

export function validateAnnouncementMedia(file: File): string | null {
  if (!ANNOUNCEMENT_ALLOWED_MIME_TYPES.includes(file.type)) {
    return 'Unsupported format. Use JPG, PNG, WEBP, MP4, or MOV.';
  }
  if (file.size > ANNOUNCEMENT_MAX_FILE_SIZE_BYTES) {
    return `File size must be ${ANNOUNCEMENT_MAX_FILE_SIZE_MB} MB or less.`;
  }
  return null;
}

async function getMediaUploadUrl(body: TAnnouncementMediaUploadUrlRequest) {
  return await request<TAnnouncementMediaUploadUrlResponse>({
    url: 'announcements/media/upload-url/',
    method: 'POST',
    body,
  });
}

function uploadToS3PresignedPost({
  url,
  fields,
  file,
  onProgress,
  signal,
}: {
  url: string;
  fields: Record<string, string>;
  file: File;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload aborted', 'AbortError'));
      });
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed due to a network error'));
    };

    xhr.send(formData);
  });
}

export async function uploadAnnouncementMedia(
  file: File,
  options?: {
    onProgress?: (progress: number) => void;
    signal?: AbortSignal;
  }
): Promise<string> {
  const validationError = validateAnnouncementMedia(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const { key, url, fields } = await getMediaUploadUrl({
    file_name: file.name,
    content_type: file.type,
    size: file.size,
  });

  await uploadToS3PresignedPost({
    url,
    fields,
    file,
    onProgress: options?.onProgress,
    signal: options?.signal,
  });

  return key;
}
